import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import "./CinematicChapterPage.css";

const clamp = (value, min = 0, max = 1) =>
  Math.min(max, Math.max(min, value));

const lerp = (a, b, t) =>
  a + (b - a) * t;

const ease = (t) =>
  t * t * (3 - 2 * t);

const CinematicChapterPage = ({
  chapter,
  onBack,
}) => {
  const sequenceRef = useRef(null);

  const rafRef = useRef(null);

  const wheelLockRef = useRef(false);

  const touchStartRef = useRef(0);

  const [progress, setProgress] =
    useState(0);

  /*
  ============================================================
  ALL PHOTOS
  ============================================================
  */

  const images = useMemo(() => {
    const list =
      chapter?.images?.length > 0
        ? chapter.images
        : [
            chapter?.image,
            ...(chapter?.grid || []),
          ];

    return list.filter(Boolean);
  }, [chapter]);


  /*
  ============================================================
  START FROM TOP
  ============================================================
  */

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  /*
  ============================================================
  NORMAL SCROLL PROGRESS
  ============================================================
  */

  useEffect(() => {
    const updateProgress = () => {
      if (rafRef.current) {
        cancelAnimationFrame(
          rafRef.current
        );
      }

      rafRef.current =
        requestAnimationFrame(() => {
          const section =
            sequenceRef.current;

          if (!section) return;

          const rect =
            section.getBoundingClientRect();

          const scrollable =
            section.offsetHeight -
            window.innerHeight;

          const passed =
            -rect.top;

          const value = clamp(
            passed /
              Math.max(
                scrollable,
                1
              )
          );

          setProgress(value);
        });
    };

    window.addEventListener(
      "scroll",
      updateProgress,
      {
        passive: true,
      }
    );

    window.addEventListener(
      "resize",
      updateProgress
    );

    updateProgress();

    return () => {
      window.removeEventListener(
        "scroll",
        updateProgress
      );

      window.removeEventListener(
        "resize",
        updateProgress
      );

      if (rafRef.current) {
        cancelAnimationFrame(
          rafRef.current
        );
      }
    };
  }, []);


  /*
  ============================================================
  CINEMATIC TIMELINE

  One complete photo gets its own
  scroll position.

  Last photo stays visible at the end.
  ============================================================
  */

  const photoTimeline =
    images.length <= 1
      ? 0
      : Math.min(
          images.length - 1,
          progress *
            (images.length - 1)
        );


  /*
  ============================================================
  ACTIVE PHOTO
  ============================================================
  */

  const activeIndex = clamp(
    Math.round(photoTimeline),
    0,
    Math.max(
      images.length - 1,
      0
    )
  );


  /*
  ============================================================
  PHOTO ENTRY DIRECTIONS

  Every photo gets a different movement.
  ============================================================
  */

  const directions = [
    {
      x: 45,
      y: -18,
      rotate: 8,
    },

    {
      x: -45,
      y: 15,
      rotate: -8,
    },

    {
      x: 34,
      y: 30,
      rotate: 6,
    },

    {
      x: -38,
      y: -26,
      rotate: -7,
    },

    {
      x: 48,
      y: 5,
      rotate: 10,
    },

    {
      x: -48,
      y: 10,
      rotate: -10,
    },

    {
      x: 25,
      y: -34,
      rotate: 5,
    },

    {
      x: -25,
      y: 34,
      rotate: -5,
    },
  ];


  /*
  ============================================================
  GET PHOTO STYLE
  ============================================================
  */

  const getPhotoStyle = (
    index
  ) => {
    const distance =
      photoTimeline - index;

    const direction =
      directions[
        index %
          directions.length
      ];

    /*
    ------------------------------------------------------------
    FAR FUTURE
    ------------------------------------------------------------
    */

    if (distance < -1.25) {
      return {
        opacity: 0,

        visibility:
          "hidden",

        transform: `
          translate3d(
            ${direction.x}vw,
            ${direction.y}vh,
            0
          )
          rotate(
            ${direction.rotate}deg
          )
          scale(.68)
        `,

        zIndex: 1,
      };
    }


    /*
    ------------------------------------------------------------
    NEXT PHOTO ENTERING
    ------------------------------------------------------------
    */

    if (distance < 0) {
      const t = clamp(
        (distance + 1.25) /
          1.25
      );

      const e = ease(t);

      return {
        opacity: e,

        visibility:
          "visible",

        transform: `
          translate3d(
            ${lerp(
              direction.x,
              0,
              e
            )}vw,

            ${lerp(
              direction.y,
              0,
              e
            )}vh,

            0
          )

          rotate(
            ${lerp(
              direction.rotate,
              0,
              e
            )}deg
          )

          scale(
            ${lerp(
              0.68,
              1,
              e
            )}
          )
        `,

        zIndex:
          300 + index,
      };
    }


    /*
    ------------------------------------------------------------
    CURRENT PHOTO

    Slowly leaves center while next
    photo starts entering.
    ------------------------------------------------------------
    */

    if (distance <= 1) {
      const t = clamp(
        distance
      );

      const e = ease(t);

      const exitX =
        index % 2 === 0
          ? -18
          : 18;

      const exitY =
        index % 3 === 0
          ? -7
          : 6;

      const exitRotate =
        index % 2 === 0
          ? -4
          : 4;

      return {
        opacity: lerp(
          1,
          0.3,
          e
        ),

        visibility:
          "visible",

        transform: `
          translate3d(
            ${lerp(
              0,
              exitX,
              e
            )}vw,

            ${lerp(
              0,
              exitY,
              e
            )}vh,

            0
          )

          rotate(
            ${lerp(
              0,
              exitRotate,
              e
            )}deg
          )

          scale(
            ${lerp(
              1,
              0.87,
              e
            )}
          )
        `,

        zIndex:
          300 + index,
      };
    }


    /*
    ------------------------------------------------------------
    OLD PHOTO

    Leaves a small editorial layer behind.
    ------------------------------------------------------------
    */

    if (distance <= 1.7) {
      const t = clamp(
        (distance - 1) /
          0.7
      );

      const e = ease(t);

      const x =
        index % 2 === 0
          ? -23
          : 23;

      const y =
        index % 3 === 0
          ? -10
          : 9;

      return {
        opacity: lerp(
          0.3,
          0,
          e
        ),

        visibility:
          "visible",

        transform: `
          translate3d(
            ${lerp(
              index % 2 === 0
                ? -18
                : 18,
              x,
              e
            )}vw,

            ${lerp(
              index % 3 === 0
                ? -7
                : 6,
              y,
              e
            )}vh,

            0
          )

          rotate(
            ${index % 2 === 0
              ? -6
              : 6}deg
          )

          scale(
            ${lerp(
              0.87,
              0.73,
              e
            )}
          )
        `,

        zIndex:
          150 + index,
      };
    }


    /*
    ------------------------------------------------------------
    HIDDEN
    ------------------------------------------------------------
    */

    return {
      opacity: 0,

      visibility:
        "hidden",

      transform:
        "translate3d(0,0,0) scale(.7)",

      zIndex: 1,
    };
  };


  /*
  ============================================================
  FIND SEQUENCE POSITION
  ============================================================
  */

  const getSequenceMetrics =
    () => {
      const section =
        sequenceRef.current;

      if (!section) {
        return null;
      }

      const rect =
        section.getBoundingClientRect();

      const sequenceTop =
        window.scrollY +
        rect.top;

      const totalScrollable =
        section.offsetHeight -
        window.innerHeight;

      const step =
        totalScrollable /
        Math.max(
          images.length - 1,
          1
        );

      return {
        sequenceTop,
        totalScrollable,
        step,
      };
    };


  /*
  ============================================================
  ONE WHEEL = ONE PHOTO

  IMPORTANT:

  We don't allow browser's normal huge
  wheel movement to skip photographs.
  ============================================================
  */

  useEffect(() => {
    const handleWheel = (
      event
    ) => {
      const section =
        sequenceRef.current;

      if (!section) return;

      const rect =
        section.getBoundingClientRect();

      const viewportHeight =
        window.innerHeight;

      /*
      Is cinematic stage locked
      on screen?
      */

      const inside =
        rect.top <= 2 &&
        rect.bottom >=
          viewportHeight - 2;

      if (!inside) {
        return;
      }

      /*
      Small trackpad noise.
      */

      if (
        Math.abs(
          event.deltaY
        ) < 10
      ) {
        return;
      }

      /*
      IMPORTANT:
      Stop browser from doing
      continuous scrolling.
      */

      event.preventDefault();

      /*
      If transition is already running,
      ignore additional wheel.
      */

      if (
        wheelLockRef.current
      ) {
        return;
      }

      wheelLockRef.current =
        true;

      const direction =
        event.deltaY > 0
          ? 1
          : -1;

      const metrics =
        getSequenceMetrics();

      if (!metrics) {
        wheelLockRef.current =
          false;

        return;
      }

      const {
        sequenceTop,
        totalScrollable,
        step,
      } = metrics;

      /*
      Current exact photo.
      */

      const currentScroll =
        window.scrollY -
        sequenceTop;

      let currentIndex =
        Math.round(
          currentScroll /
            step
        );

      currentIndex =
        Math.max(
          0,
          Math.min(
            images.length - 1,
            currentIndex
          )
        );

      /*
      ONE PHOTO ONLY
      */

      let nextIndex =
        currentIndex +
        direction;

      /*
      ----------------------------------------------------------
      GOING ABOVE FIRST PHOTO
      ----------------------------------------------------------
      */

      if (
        direction < 0 &&
        currentIndex === 0
      ) {
        wheelLockRef.current =
          false;

        window.scrollTo({
          top:
            sequenceTop -
            10,

          behavior:
            "smooth",
        });

        return;
      }


      /*
      ----------------------------------------------------------
      GOING BELOW LAST PHOTO
      ----------------------------------------------------------
      */

      if (
        direction > 0 &&
        currentIndex ===
          images.length - 1
      ) {
        wheelLockRef.current =
          false;

        window.scrollTo({
          top:
            sequenceTop +
            totalScrollable +
            30,

          behavior:
            "smooth",
        });

        return;
      }


      /*
      Keep index safe.
      */

      nextIndex =
        Math.max(
          0,
          Math.min(
            images.length - 1,
            nextIndex
          )
        );


      /*
      Exact destination.
      */

      const target =
        sequenceTop +
        nextIndex * step;


      /*
      Cinematic smooth movement.
      */

      window.scrollTo({
        top: target,

        behavior:
          "smooth",
      });


      /*
      Lock the wheel while
      photo transition happens.
      */

      setTimeout(() => {
        wheelLockRef.current =
          false;
      }, 900);
    };


    window.addEventListener(
      "wheel",
      handleWheel,
      {
        passive: false,
      }
    );


    return () => {
      window.removeEventListener(
        "wheel",
        handleWheel
      );
    };
  }, [images.length]);


  /*
  ============================================================
  TOUCH / MOBILE SWIPE

  One swipe = one photo.
  ============================================================
  */

  useEffect(() => {
    const section =
      sequenceRef.current;

    if (!section) return;


    const handleTouchStart =
      (event) => {
        touchStartRef.current =
          event.touches[0].clientY;
      };


    const handleTouchEnd =
      (event) => {
        const start =
          touchStartRef.current;

        const end =
          event.changedTouches[0]
            .clientY;

        const distance =
          start - end;

        if (
          Math.abs(distance) <
          45
        ) {
          return;
        }

        /*
        Reuse wheel-style movement.
        */

        const direction =
          distance > 0
            ? 1
            : -1;

        const rect =
          section.getBoundingClientRect();

        const inside =
          rect.top <= 2 &&
          rect.bottom >=
            window.innerHeight - 2;

        if (!inside) return;

        const metrics =
          getSequenceMetrics();

        if (!metrics) return;

        const {
          sequenceTop,
          totalScrollable,
          step,
        } = metrics;

        const currentScroll =
          window.scrollY -
          sequenceTop;

        let currentIndex =
          Math.round(
            currentScroll /
              step
          );

        currentIndex =
          Math.max(
            0,
            Math.min(
              images.length - 1,
              currentIndex
            )
          );

        if (
          direction < 0 &&
          currentIndex === 0
        ) {
          return;
        }

        if (
          direction > 0 &&
          currentIndex ===
            images.length - 1
        ) {
          window.scrollTo({
            top:
              sequenceTop +
              totalScrollable +
              30,

            behavior:
              "smooth",
          });

          return;
        }

        const nextIndex =
          Math.max(
            0,
            Math.min(
              images.length - 1,
              currentIndex +
                direction
            )
          );

        window.scrollTo({
          top:
            sequenceTop +
            nextIndex * step,

          behavior:
            "smooth",
        });
      };


    section.addEventListener(
      "touchstart",
      handleTouchStart,
      {
        passive: true,
      }
    );

    section.addEventListener(
      "touchend",
      handleTouchEnd,
      {
        passive: true,
      }
    );


    return () => {
      section.removeEventListener(
        "touchstart",
        handleTouchStart
      );

      section.removeEventListener(
        "touchend",
        handleTouchEnd
      );
    };
  }, [images.length]);


  /*
  ============================================================
  30 PHOTO SEQUENCE HEIGHT

  30 × 180vh = 5400vh
  ============================================================
  */

  const sequenceHeight =
    Math.max(
      images.length * 180,
      180
    );


  /*
  ============================================================
  RENDER
  ============================================================
  */

  return (
    <main className="ccp-page">

      {/* ======================================================
          TOP BAR
      ====================================================== */}

      <header className="ccp-topbar">

        <button
          className="ccp-back"
          onClick={onBack}
        >
          ← BACK TO PHOTOGRAPHY
        </button>


        <div className="ccp-top-right">

          <span>
            {chapter.number}
          </span>

          <span className="ccp-top-divider">
            /
          </span>

          <span>
            {String(
              images.length
            ).padStart(2, "0")}
          </span>

        </div>

      </header>


      {/* ======================================================
          INTRO
      ====================================================== */}

      <section className="ccp-intro">

        <div className="ccp-intro-small">
          THE CULTURE CAPSULE
        </div>


        <div className="ccp-intro-number">
          CHAPTER {chapter.number}
        </div>


        <h1>
          {chapter.title}
        </h1>


        <div className="ccp-intro-bottom">

          <p>
            {chapter.description}
            <br />
            {chapter.description2}
          </p>


          <div className="ccp-scroll-indicator">

            <span>
              SCROLL TO ENTER
            </span>

            <div className="ccp-scroll-line">
              <i />
            </div>

          </div>

        </div>

      </section>


      {/* ======================================================
          CINEMATIC GALLERY
      ====================================================== */}

      <section
        ref={sequenceRef}
        className="ccp-sequence"
        style={{
          height:
            `${sequenceHeight}vh`,
        }}
      >

        <div className="ccp-sticky">

          {/* atmospheric layers */}

          <div className="ccp-light-orb" />

          <div className="ccp-grain" />


          {/* ==================================================
              PHOTOS
          ================================================== */}

          {images.map(
            (
              image,
              index
            ) => {

              const style =
                getPhotoStyle(
                  index
                );

              return (
                <figure
                  key={`${image}-${index}`}
                  className={`
                    ccp-photo
                    ccp-photo-${index % 6}
                  `}
                  style={style}
                >

                  <img
                    src={image}
                    alt={`
                      ${chapter.title}
                      —
                      Frame ${index + 1}
                    `}
                    draggable="false"
                  />


                  <div className="ccp-photo-number">

                    {String(
                      index + 1
                    ).padStart(
                      2,
                      "0"
                    )}

                  </div>

                </figure>
              );
            }
          )}


          {/* ==================================================
              CENTER BRAND
          ================================================== */}

          <div
            className="ccp-center-mark"
            style={{
              opacity:
                progress < 0.055
                  ? 1 -
                    progress /
                      0.055
                  : 0,

              transform: `
                translate(
                  -50%,
                  -50%
                )
                scale(
                  ${
                    1 +
                    progress *
                      0.15
                  }
                )
              `,
            }}
          >

            <span>
              PHOTOGRAPHY
            </span>

            <strong>
              {chapter.title}
            </strong>

          </div>


          {/* ==================================================
              SIDE META
          ================================================== */}

          <div className="ccp-side-meta">

            <span>
              TCC / VISUAL ARCHIVE
            </span>

            <span>
              {chapter.number}
            </span>

          </div>


          {/* ==================================================
              FRAME COUNTER
          ================================================== */}

          <div className="ccp-image-caption">

            <span>
              FRAME
            </span>

            <strong>
              {String(
                activeIndex + 1
              ).padStart(
                2,
                "0"
              )}
            </strong>

          </div>


          {/* ==================================================
              BOTTOM PROGRESS
          ================================================== */}

          <div className="ccp-bottom-meta">

            <span>
              {String(
                activeIndex + 1
              ).padStart(
                2,
                "0"
              )}
            </span>


            <div className="ccp-progress-track">

              <i
                style={{
                  transform:
                    `scaleX(${progress})`,
                }}
              />

            </div>


            <span>
              {String(
                images.length
              ).padStart(
                2,
                "0"
              )}
            </span>

          </div>

        </div>

      </section>


      {/* ======================================================
          END OF CHAPTER
      ====================================================== */}

      <section className="ccp-ending">

        <div className="ccp-ending-inner">

          <span className="ccp-ending-kicker">
            END OF CHAPTER
          </span>


          <div className="ccp-ending-line" />


          <h2>
            {chapter.title}
          </h2>


          <p>
            A collection of moments,
            people and perspectives —
            preserved through the lens.
          </p>


          <button
            onClick={onBack}
          >
            ← EXPLORE ALL CHAPTERS
          </button>

        </div>

      </section>

    </main>
  );
};

export default CinematicChapterPage;